<template>
  <div class="page-container">
    <!-- 欢迎区域 -->
    <div class="welcome-section">
      <div class="welcome-left">
        <h2 class="welcome-title">控制台</h2>
        <p class="welcome-sub">实时掌握文章生成、发布数据与全网热点动态</p>
      </div>
      <div class="welcome-right">
        <span class="welcome-date">{{ todayDate }}</span>
      </div>
    </div>

    <!-- 数据卡片区域 -->
    <div class="stats-row">
      <div
        v-for="(item, index) in stats"
        :key="index"
        class="stat-card"
        :class="'stat-card--' + item.color"
      >
        <div class="stat-card__bar"></div>
        <div class="stat-card__body">
          <div class="stat-card__left">
            <div class="stat-card__label">{{ item.label }}</div>
            <div class="stat-card__value">
              <template v-if="item.loading">
                <div class="skeleton-line"></div>
              </template>
              <span v-else class="stat-number">{{ item.value }}</span>
            </div>
          </div>
          <div class="stat-card__icon">
            <el-icon><component :is="item.icon" /></el-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="charts-row">
      <div class="chart-panel">
        <div class="panel-header">
          <div class="panel-header__left">
            <span class="panel-dot panel-dot--blue"></span>
            <span class="panel-title">生成文章趋势</span>
          </div>
          <span class="panel-sub">近 7 日</span>
        </div>
        <div class="panel-body">
          <div id="generateChart" class="chart-canvas"></div>
        </div>
      </div>

      <div class="chart-panel">
        <div class="panel-header">
          <div class="panel-header__left">
            <span class="panel-dot panel-dot--green"></span>
            <span class="panel-title">发布文章趋势</span>
          </div>
          <span class="panel-sub">近 7 日</span>
        </div>
        <div class="panel-body">
          <div id="publishChart" class="chart-canvas"></div>
        </div>
      </div>
    </div>

    <!-- 热点数据区域 -->
    <div class="hotspot-section">
      <div class="section-title">全网热点</div>
      <div class="hotspot-grid">
        <!-- 抖音 -->
        <div class="hotspot-panel">
          <div class="hotspot-panel__header hotspot-panel__header--douyin">
            <span class="hotspot-panel__name">抖音热榜</span>
            <span class="hotspot-panel__badge">DOUYIN</span>
          </div>
          <div class="hotspot-panel__list">
            <div
              v-for="(item, index) in douyinData"
              :key="'dy-' + index"
              class="hotspot-row"
              @click="openLink(item.url)"
            >
              <span class="hotspot-row__rank" :class="'rank-' + (index + 1)">{{ index + 1 }}</span>
              <span class="hotspot-row__title">{{ item.title }}</span>
              <span class="hotspot-row__extra">{{ formatHot(item.hot) }}</span>
            </div>
          </div>
        </div>

        <!-- 微博 -->
        <div class="hotspot-panel">
          <div class="hotspot-panel__header hotspot-panel__header--weibo">
            <span class="hotspot-panel__name">微博热搜</span>
            <span class="hotspot-panel__badge">WEIBO</span>
          </div>
          <div class="hotspot-panel__list">
            <div
              v-for="(item, index) in weiboData"
              :key="'wb-' + index"
              class="hotspot-row"
              @click="openLink(item.url)"
            >
              <span class="hotspot-row__rank" :class="'rank-' + (index + 1)">{{ index + 1 }}</span>
              <span class="hotspot-row__title">{{ item.title }}</span>
              <span class="hotspot-row__extra">{{ formatHot(item.hot) }}</span>
            </div>
          </div>
        </div>

        <!-- 腾讯新闻 -->
        <div class="hotspot-panel">
          <div class="hotspot-panel__header hotspot-panel__header--tencent">
            <span class="hotspot-panel__name">腾讯新闻</span>
            <span class="hotspot-panel__badge">TENCENT</span>
          </div>
          <div class="hotspot-panel__list">
            <div
              v-for="(item, index) in zhihuData"
              :key="'tx-' + index"
              class="hotspot-row"
              @click="openLink(item.url)"
            >
              <span class="hotspot-row__rank" :class="'rank-' + (index + 1)">{{ index + 1 }}</span>
              <span class="hotspot-row__title">{{ item.title }}</span>
              <span class="hotspot-row__extra hotspot-row__author">{{ item.author }}</span>
            </div>
          </div>
        </div>

        <!-- 百度 -->
        <div class="hotspot-panel">
          <div class="hotspot-panel__header hotspot-panel__header--baidu">
            <span class="hotspot-panel__name">百度热搜</span>
            <span class="hotspot-panel__badge">BAIDU</span>
          </div>
          <div class="hotspot-panel__list">
            <div
              v-for="(item, index) in baiduData"
              :key="'bd-' + index"
              class="hotspot-row"
              @click="openLink(item.url)"
            >
              <span class="hotspot-row__rank" :class="'rank-' + (index + 1)">{{ index + 1 }}</span>
              <span class="hotspot-row__title">{{ item.title }}</span>
              <span class="hotspot-row__extra">{{ formatHot(item.hot) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  TrendCharts,
  Document,
  Upload,
  Collection,
  Picture,
  DataLine,
  User
} from '@element-plus/icons-vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
  name: 'AppDashboard',
  components: {
    TrendCharts,
    Document,
    Upload,
    Collection,
    Picture,
    DataLine,
    User
  },
  data() {
    return {
      stats: [
        { label: '公众号数量', value: 0, loading: true, icon: 'User', key: 'total_accounts', action: 1, color: 'blue' },
        { label: '文章数量', value: 0, loading: true, icon: 'Document', key: 'total_articles', action: 2, color: 'indigo' },
        { label: '总发布文章', value: 0, loading: true, icon: 'Upload', key: 'total_published', action: 3, color: 'green' },
        { label: '今日发布', value: 0, loading: true, icon: 'TrendCharts', key: 'today_published', action: 3, color: 'emerald' },
        { label: '标题数量', value: 0, loading: true, icon: 'Collection', key: 'total_titles', action: 5, color: 'amber' },
        { label: '图库数量', value: 0, loading: true, icon: 'Picture', key: 'total_images', action: 4, color: 'rose' }
      ],
      douyinData: [],
      weiboData: [],
      zhihuData: [],
      baiduData: [],
      dailyGenerated: [],
      dailyPublished: [],
      resizeTimer: null
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    todayDate() {
      const d = new Date()
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      return year + '-' + month + '-' + day + ' ' + weekdays[d.getDay()]
    }
  },
  mounted() {
    this.initCharts()
    this.fetchHotData()
    this.fetchDashboardStats()
    this.fetchChartData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.genChart) this.genChart.dispose()
    if (this.pubChart) this.pubChart.dispose()
  },
  methods: {
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.genChart) this.genChart.resize()
        if (this.pubChart) this.pubChart.resize()
      }, 150)
    },

    fetchHotData() {
      this.fetchDouyin()
      this.fetchWeibo()
      this.fetchZhihu()
      this.fetchBaidu()
    },

    async fetchDouyin() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=douyin')
        const data = await response.json()
        if (data.code === 200) {
          this.douyinData = data.data.slice(0, 10) || []
        }
      } catch (error) {
        console.error('抖音数据获取失败:', error)
      }
    },

    async fetchWeibo() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=weibo')
        const data = await response.json()
        if (data.code === 200) {
          this.weiboData = data.data.slice(0, 10) || []
        }
      } catch (error) {
        console.error('微博数据获取失败:', error)
      }
    },

    async fetchZhihu() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=qqnews')
        const data = await response.json()
        if (data.code === 200) {
          this.zhihuData = data.data.slice(0, 10) || []
        }
      } catch (error) {
        console.error('腾讯新闻数据获取失败:', error)
      }
    },

    async fetchBaidu() {
      try {
        const response = await fetch('https://gzh.aijzdi.com/today_hot_api.php?action=getHotList&platform=baidu')
        const data = await response.json()
        if (data.code === 200) {
          this.baiduData = data.data.slice(0, 10) || []
        }
      } catch (error) {
        console.error('百度数据获取失败:', error)
      }
    },

    async fetchChartData() {
      if (!this.userInfo || !this.userInfo.id) return
      try {
        const [genRes, pubRes] = await Promise.all([
          axios.post(`${config.BASE_URL}/shop/api/data_statistics.php`, {
            store_id: this.userInfo.id,
            action: 6
          }),
          axios.post(`${config.BASE_URL}/shop/api/data_statistics.php`, {
            store_id: this.userInfo.id,
            action: 7
          })
        ])

        if (genRes.data && genRes.data.daily_generated) {
          this.dailyGenerated = genRes.data.daily_generated
        }
        if (pubRes.data && pubRes.data.daily_published) {
          this.dailyPublished = pubRes.data.daily_published
        }

        this.updateCharts()
      } catch (error) {
        console.error('获取图表数据失败:', error)
      }
    },

    initCharts() {
      this.genChart = echarts.init(document.getElementById('generateChart'))
      this.pubChart = echarts.init(document.getElementById('publishChart'))
      this.updateCharts()
    },

    updateCharts() {
      const genDates = this.dailyGenerated.map(i => i.date) || []
      const genCounts = this.dailyGenerated.map(i => i.count) || []
      const pubDates = this.dailyPublished.map(i => i.date) || []
      const pubCounts = this.dailyPublished.map(i => i.count) || []

      const baseGrid = { top: 20, right: 20, bottom: 30, left: 40 }
      const baseXAxis = {
        type: 'category',
        data: genDates,
        axisLine: { lineStyle: { color: '#e2e8f0' } },
        axisTick: { show: false },
        axisLabel: { color: '#94a3b8', fontSize: 11 }
      }

      this.genChart.setOption({
        grid: baseGrid,
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          borderColor: '#e2e8f0',
          textStyle: { color: '#334155', fontSize: 12 },
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        },
        xAxis: { ...baseXAxis, data: genDates },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#f1f5f9' } },
          axisLabel: { color: '#94a3b8', fontSize: 11 }
        },
        series: [{
          name: '生成量',
          type: 'bar',
          data: genCounts,
          barWidth: 24,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#3b82f6' },
              { offset: 1, color: '#93c5fd' }
            ])
          },
          label: {
            show: true,
            position: 'top',
            color: '#64748b',
            fontSize: 11,
            fontWeight: 600
          }
        }]
      })

      this.pubChart.setOption({
        grid: baseGrid,
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          borderColor: '#e2e8f0',
          textStyle: { color: '#334155', fontSize: 12 },
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        },
        xAxis: { ...baseXAxis, data: pubDates },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#f1f5f9' } },
          axisLabel: { color: '#94a3b8', fontSize: 11 }
        },
        series: [{
          name: '发布量',
          type: 'line',
          data: pubCounts,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: '#22c55e', width: 2 },
          itemStyle: { color: '#22c55e' },
          label: {
            show: true,
            position: 'top',
            color: '#64748b',
            fontSize: 11,
            fontWeight: 600
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(34,197,94,0.12)' },
              { offset: 1, color: 'rgba(34,197,94,0)' }
            ])
          }
        }]
      })
    },

    formatHot(hot) {
      if (!hot) return ''
      if (hot >= 10000) {
        return (hot / 10000).toFixed(1) + '万'
      }
      return String(hot)
    },

    openLink(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },

    async fetchDashboardStats() {
      if (!this.userInfo || !this.userInfo.id) {
        this.stats.forEach(s => { s.value = '--'; s.loading = false })
        return
      }

      try {
        const uniqueActions = [...new Set(this.stats.map(s => s.action))]
        const actionCards = {}
        this.stats.forEach(s => {
          if (!actionCards[s.action]) actionCards[s.action] = []
          actionCards[s.action].push({ key: s.key, index: this.stats.indexOf(s) })
        })

        const requests = uniqueActions.map(action =>
          axios.post(`${config.BASE_URL}/shop/api/data_statistics.php`, {
            store_id: this.userInfo.id,
            action: action
          })
        )

        const responses = await Promise.all(requests)

        responses.forEach((response, idx) => {
          const data = response.data
          const action = uniqueActions[idx]

          if (!data.error) {
            actionCards[action].forEach(cardInfo => {
              const stat = this.stats[cardInfo.index]
              stat.loading = false
              if (action === 3) {
                stat.value = cardInfo.key === 'total_published'
                  ? (data.total_published || 0)
                  : (data.today_published || 0)
              } else {
                stat.value = data[stat.key] || 0
              }
            })
          } else {
            actionCards[action].forEach(cardInfo => {
              this.stats[cardInfo.index].value = '--'
              this.stats[cardInfo.index].loading = false
            })
          }
        })
      } catch (error) {
        console.error('获取统计数据失败:', error)
        this.stats.forEach(s => { s.value = '--'; s.loading = false })
      }
    }
  }
}
</script>

<style scoped lang="scss">
$radius: 10px;
$text-primary: #1e293b;
$text-secondary: #64748b;
$border-light: #e2e8f0;

.page-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}

/* ========== Welcome Section ========== */
.welcome-section {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
}

.welcome-title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: $text-primary;
  letter-spacing: -0.3px;
}

.welcome-sub {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: $text-secondary;
}

.welcome-date {
  font-size: 13px;
  color: $text-secondary;
  background: #fff;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid $border-light;
}

/* ========== Stats Cards ========== */
.stats-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  position: relative;
  background: #ffffff;
  border-radius: $radius;
  border: 1px solid $border-light;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }

  &__bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
  }

  &--blue    &__bar { background: #3b82f6; }
  &--indigo  &__bar { background: #6366f1; }
  &--green   &__bar { background: #22c55e; }
  &--emerald &__bar { background: #10b981; }
  &--amber   &__bar { background: #f59e0b; }
  &--rose    &__bar { background: #f43f5e; }

  &__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px 18px 22px;
  }

  &__label {
    font-size: 13px;
    color: $text-secondary;
    margin-bottom: 6px;
  }

  &__value {
    min-height: 28px;
    display: flex;
    align-items: center;
  }

  .stat-number {
    font-size: 26px;
    font-weight: 700;
    color: $text-primary;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.5px;
  }

  .skeleton-line {
    width: 48px;
    height: 22px;
    background: #f1f5f9;
    border-radius: 4px;
    animation: pulse 1.5s ease-in-out infinite;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  &--blue    &__icon { background: #eff6ff; color: #3b82f6; }
  &--indigo  &__icon { background: #eef2ff; color: #6366f1; }
  &--green   &__icon { background: #f0fdf4; color: #22c55e; }
  &--emerald &__icon { background: #ecfdf5; color: #10b981; }
  &--amber   &__icon { background: #fffbeb; color: #f59e0b; }
  &--rose    &__icon { background: #fff1f2; color: #f43f5e; }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

/* ========== Chart Panels ========== */
.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.chart-panel {
  background: #ffffff;
  border-radius: $radius;
  border: 1px solid $border-light;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.panel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &--blue  { background: #3b82f6; }
  &--green { background: #22c55e; }
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
}

.panel-sub {
  font-size: 12px;
  color: $text-secondary;
}

.panel-body {
  padding: 8px 8px 0;
}

.chart-canvas {
  height: 280px;
}

/* ========== Hotspot Section ========== */
.hotspot-section {
  margin-bottom: 8px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 16px;
  letter-spacing: -0.2px;
}

.hotspot-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.hotspot-panel {
  background: #ffffff;
  border-radius: $radius;
  border: 1px solid $border-light;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid #f1f5f9;
  }

  &__header--douyin  { border-left: 3px solid #fe2c55; }
  &__header--weibo   { border-left: 3px solid #e71f19; }
  &__header--tencent { border-left: 3px solid #0084ff; }
  &__header--baidu   { border-left: 3px solid #2932e1; }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: $text-primary;
  }

  &__badge {
    font-size: 10px;
    font-weight: 600;
    color: $text-secondary;
    letter-spacing: 0.8px;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 4px;
  }

  &__list {
    padding: 6px 0;
  }
}

.hotspot-row {
  display: flex;
  align-items: center;
  padding: 8px 18px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  &__rank {
    width: 20px;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    flex-shrink: 0;
    margin-right: 10px;
    color: $text-secondary;
    font-variant-numeric: tabular-nums;
  }

  .rank-1 { color: #fe2c55; }
  .rank-2 { color: #f97316; }
  .rank-3 { color: #eab308; }

  &__title {
    flex: 1;
    font-size: 13px;
    color: $text-primary;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__extra {
    font-size: 11.5px;
    color: $text-secondary;
    margin-left: 8px;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }

  &__author {
    font-style: italic;
    max-width: 56px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
